import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Nav } from "../components/Nav";
import { Footer } from "../components/Footer";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

// Earthy palette matching the source assessment
const PALETTE = {
  soil: "#191C14", soil2: "#232719", wheat: "#E9E1C8",
  wheatDim: "#B9B091", blade: "#5C8A3C", grain: "#C9A227",
};

const SCALE_LABELS = ["Never / not yet", "Rarely", "Sometimes", "Often", "Consistently"];

const Stalk = ({ progress, steps }) => {
  // 9 vertical segments — each fills as the user completes questions in the corresponding step group.
  return (
    <div className="hidden md:flex flex-col items-center gap-2 fixed left-8 top-1/2 -translate-y-1/2 z-40" data-testid="bg-stalk" aria-hidden>
      {steps.map((_, i) => {
        const segProgress = Math.min(1, Math.max(0, progress * 9 - i));
        return (
          <div key={i} className="relative w-[3px] h-8 bg-[#E9E1C8]/12">
            <div
              className="absolute inset-x-0 bottom-0"
              style={{ height: `${segProgress * 100}%`, background: i < 3 ? PALETTE.blade : i < 6 ? PALETTE.grain : PALETTE.wheat, transition: "height 350ms" }}
            />
          </div>
        );
      })}
    </div>
  );
};

export default function BladeGrain() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [steps, setSteps] = useState([]);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [started, setStarted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    document.title = "The Blade & The Grain · Assessment";
    api.get("/blade-grain/items").then(({ data }) => {
      setItems(data.items);
      setSteps(data.steps);
    }).catch(() => toast.error("Could not load the assessment."));
  }, []);

  const total = items.length;
  const progress = total ? idx / total : 0;

  const pick = (val) => {
    const next = [...answers, val];
    setAnswers(next);
    if (idx + 1 < total) {
      setTimeout(() => setIdx(idx + 1), 180);
    } else {
      submit(next);
    }
  };

  const goBack = () => {
    if (idx === 0) return;
    setAnswers(answers.slice(0, -1));
    setIdx(idx - 1);
  };

  const submit = async (final) => {
    if (!user) {
      localStorage.setItem("lwn_pending_bg", JSON.stringify(final));
      toast("Create a free account to save your Blade & Grain result.");
      nav("/signup");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/blade-grain/submit", { answers: final });
      setResult(data);
    } catch {
      toast.error("Could not submit. Please try again.");
      setSubmitting(false);
    }
  };

  useEffect(() => {
    const pending = localStorage.getItem("lwn_pending_bg");
    if (user && pending) {
      localStorage.removeItem("lwn_pending_bg");
      submit(JSON.parse(pending));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (submitting && !result) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 text-center" style={{ background: PALETTE.soil, color: PALETTE.wheat }}>
        <div>
          <p className="font-serif-d text-3xl italic-emph animate-pulse" style={{ color: PALETTE.grain }}>Reading the grain…</p>
          <p className="mt-4 opacity-70">Locating where you are in the nine steps.</p>
        </div>
      </div>
    );
  }

  if (result) return <BGResult result={result} steps={steps} onRetake={() => { setResult(null); setStarted(false); setIdx(0); setAnswers([]); setSubmitting(false); }} />;

  return (
    <div className="min-h-screen" style={{ background: PALETTE.soil, color: PALETTE.wheat }}>
      <Nav />
      <Stalk progress={progress} steps={steps} />

      <section className="max-w-3xl mx-auto px-6 pt-40 pb-24">
        {!started ? (
          <IntroCard onStart={() => setStarted(true)} />
        ) : (
          <QuizCard
            item={items[idx]}
            idx={idx}
            total={total}
            onPick={pick}
            onBack={goBack}
          />
        )}
      </section>
      <Footer />
    </div>
  );
}

const IntroCard = ({ onStart }) => (
  <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="text-center">
    <p className="eyebrow" style={{ color: PALETTE.grain }}>Plan A · Nine Steps</p>
    <h1 className="font-serif-d text-5xl sm:text-6xl mt-6 leading-[1.05]">
      The <span className="italic-emph" style={{ color: PALETTE.grain }}>Blade</span> & The <span className="italic-emph" style={{ color: PALETTE.blade }}>Grain</span>
    </h1>
    <p className="mt-8 max-w-xl mx-auto text-base leading-relaxed" style={{ color: PALETTE.wheatDim }}>
      30 statements. Nine steps. Three stages. This is a mirror, not a report card.
      Answer for the last six months — not for who you&apos;re trying to be.
    </p>
    <div className="mt-10 grid sm:grid-cols-3 gap-4 max-w-2xl mx-auto text-left">
      {[
        { name: "The Blade", note: "The survivor dismantled", color: PALETTE.blade },
        { name: "The Ear", note: "The son formed", color: PALETTE.grain },
        { name: "The Full Corn", note: "The governor revealed", color: PALETTE.wheat },
      ].map((s) => (
        <div key={s.name} className="border p-5" style={{ borderColor: "rgba(233,225,200,.16)" }}>
          <p className="text-[10px] uppercase tracking-[0.22em]" style={{ color: s.color }}>{s.name}</p>
          <p className="mt-2 text-sm" style={{ color: PALETTE.wheatDim }}>{s.note}</p>
        </div>
      ))}
    </div>
    <button
      data-testid="bg-start-btn"
      onClick={onStart}
      className="mt-12 px-10 py-5 uppercase tracking-[0.22em] text-xs font-bold transition-colors"
      style={{ background: PALETTE.grain, color: PALETTE.soil }}
    >Begin the Assessment →</button>
    <p className="mt-6 text-xs" style={{ color: PALETTE.wheatDim }}>Takes ~7 minutes · No timer · You can go back</p>
  </motion.div>
);

const QuizCard = ({ item, idx, total, onPick, onBack }) => (
  <AnimatePresence mode="wait">
    <motion.div key={idx} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.3 }}>
      <div className="flex items-center justify-between text-xs mb-8" style={{ color: PALETTE.wheatDim }}>
        <span data-testid="bg-qcount">Statement {idx + 1} of {total}</span>
        {idx > 0 && (
          <button data-testid="bg-back-btn" onClick={onBack} className="uppercase tracking-widest hover:text-[#E9E1C8]">← Back</button>
        )}
      </div>
      <p className="eyebrow" style={{ color: PALETTE.grain }}>Over the last six months</p>
      <h2 className="font-serif-d text-2xl sm:text-3xl mt-4 leading-snug" data-testid="bg-statement">{item}</h2>
      <div className="mt-10 space-y-3">
        {SCALE_LABELS.map((label, i) => (
          <button
            key={label}
            data-testid={`bg-answer-${i + 1}`}
            onClick={() => onPick(i + 1)}
            className="w-full text-left px-6 py-5 border transition-all hover:translate-x-1 group"
            style={{ borderColor: "rgba(233,225,200,.16)" }}
          >
            <div className="flex items-center gap-5">
              <span className="text-xs w-6 flex-shrink-0" style={{ color: PALETTE.grain }}>{i + 1}</span>
              <span className="text-sm sm:text-base" style={{ color: PALETTE.wheat }}>{label}</span>
            </div>
          </button>
        ))}
      </div>
    </motion.div>
  </AnimatePresence>
);

const BGResult = ({ result, steps, onRetake }) => {
  const nav = useNavigate();
  const { step, stage, profile, complete, provisional, ahead, totals } = result;
  const accent = step.stage === 0 ? PALETTE.blade : step.stage === 1 ? PALETTE.grain : PALETTE.wheat;
  return (
    <div className="min-h-screen" style={{ background: PALETTE.soil, color: PALETTE.wheat }}>
      <Nav />
      <section className="max-w-3xl mx-auto px-6 pt-40 pb-24" data-testid="bg-result">
        <p className="eyebrow" style={{ color: PALETTE.grain }}>Step {step.index + 1} of 9 · {stage.name}</p>
        <h1 className="font-serif-d text-5xl sm:text-6xl mt-5">{step.name}</h1>
        <p className="mt-3 text-lg italic" style={{ color: PALETTE.wheatDim }}>{step.subtitle}</p>

        {complete && (
          <div className="mt-6 border p-5" style={{ borderColor: accent }}>
            <p className="text-xs uppercase tracking-widest" style={{ color: accent }}>Rare Result · Reigning From Rest</p>
            <p className="mt-2 text-sm" style={{ color: PALETTE.wheatDim }}>All nine steps are established. This is uncommon — confirm with a trusted spiritual leader before assuming stewardship of others.</p>
          </div>
        )}
        {provisional && (
          <div className="mt-6 border p-5" style={{ borderColor: PALETTE.wheatDim }}>
            <p className="text-xs uppercase tracking-widest" style={{ color: PALETTE.wheatDim }}>Provisional</p>
            <p className="mt-2 text-sm" style={{ color: PALETTE.wheatDim }}>A few answers suggest aspirational scoring. Sit with the result — the truth surfaces where the strain is.</p>
          </div>
        )}

        <div className="mt-10 border p-6" style={{ borderColor: "rgba(233,225,200,.16)" }}>
          <h3 className="text-xs uppercase tracking-[0.22em]" style={{ color: accent }}>What is true of you here</h3>
          <p className="mt-4 leading-relaxed" style={{ color: PALETTE.wheat }}>{profile.t}</p>
          <div className="mt-6 pt-6 border-t" style={{ borderColor: "rgba(233,225,200,.12)" }}>
            <p className="text-xs uppercase tracking-widest mb-2" style={{ color: PALETTE.grain }}>This week</p>
            <p className="leading-relaxed">{profile.do}</p>
          </div>
          <div className="mt-5">
            <p className="text-xs uppercase tracking-widest mb-2" style={{ color: PALETTE.wheatDim }}>What not to do</p>
            <p className="leading-relaxed" style={{ color: PALETTE.wheatDim }}>{profile.dont}</p>
          </div>
        </div>

        {ahead && ahead.length > 0 && (
          <div className="mt-8 border p-6" style={{ borderColor: PALETTE.grain }}>
            <p className="text-xs uppercase tracking-[0.22em]" style={{ color: PALETTE.grain }}>Running Ahead</p>
            <p className="mt-3 text-sm" style={{ color: PALETTE.wheatDim }}>
              Your gift is running ahead of your formation on {ahead.length} later step{ahead.length > 1 ? "s" : ""}. Ability without foundation collapses under weight. Come back and complete the current step first.
            </p>
          </div>
        )}

        <div className="mt-10 border p-6" style={{ borderColor: "rgba(233,225,200,.16)" }}>
          <p className="text-xs uppercase tracking-widest" style={{ color: PALETTE.grain }}>Foundational</p>
          <p className="mt-3 text-lg font-serif-d">Whatever step you landed on — you begin in the same place.</p>
          <p className="mt-2 text-sm" style={{ color: PALETTE.wheatDim }}>Start with <em>Breaking the Cycles</em>, the deliverance workbook. Nothing built on undelivered wiring holds.</p>
        </div>

        <div className="mt-10 grid gap-3 text-sm" style={{ color: PALETTE.wheatDim }}>
          <p className="text-xs uppercase tracking-widest" style={{ color: PALETTE.wheatDim }}>Your totals per step (out of 15)</p>
          <div className="grid grid-cols-3 sm:grid-cols-9 gap-1 mt-1">
            {totals.map((t, i) => (
              <div key={i} className="text-center py-2 border" style={{ borderColor: i === step.index ? accent : "rgba(233,225,200,.16)", color: i === step.index ? accent : PALETTE.wheatDim }}>
                <div className="text-[9px] uppercase tracking-widest">{i + 1}</div>
                <div className="text-lg font-serif-d">{t}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-12 flex flex-wrap gap-3">
          <button
            data-testid="bg-retake"
            onClick={onRetake}
            className="px-8 py-4 uppercase tracking-[0.18em] text-xs border"
            style={{ borderColor: PALETTE.wheatDim, color: PALETTE.wheat }}
          >Retake</button>
          <button
            data-testid="bg-to-dashboard"
            onClick={() => nav("/dashboard")}
            className="px-8 py-4 uppercase tracking-[0.18em] text-xs font-bold"
            style={{ background: PALETTE.grain, color: PALETTE.soil }}
          >Go to Dashboard →</button>
        </div>
      </section>
      <Footer />
    </div>
  );
};
